import { css } from 'aphrodite';
import { type FC, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';
import { CountBadge } from '../../../web/components-v2/CountBadge';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import { formatOtherContacts, type TextInboxItem } from '../data';
import { IconMessageSquare } from './icons';
import { styles } from './TextInboxMenu.styles';

export type TextInboxMenuProps = {
	items: readonly TextInboxItem[];
	status?: 'loading' | 'error' | 'ready';
	onOpen: (item: TextInboxItem) => void;
};

const formatReceivedAt = (iso: string | undefined) =>
	iso
		? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
		: '';

/** Unread texts across every lead, so a reply is visible no matter which lead is open. */
export const TextInboxMenu: FC<TextInboxMenuProps> = ({ items, status = 'ready', onOpen }) => {
	const [open, setOpen] = useState(false);
	const unreadTotal = items.reduce((sum, item) => sum + item.unreadCount, 0);
	const label = unreadTotal > 0 ? `${unreadTotal} unread text${unreadTotal === 1 ? '' : 's'}` : 'Texts';

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant='ghost'
					size='icon'
					styles={[styles.trigger]}
					title={label}
					aria-label={label}
					data-testid='text-inbox-trigger'
				>
					<IconMessageSquare size={18} />
					<CountBadge count={unreadTotal} aria-hidden styles={[styles.badge]} />
				</Button>
			</PopoverTrigger>
			<PopoverContent align='start' styles={[styles.content]}>
				<div className={css(styles.header)}>Unread texts</div>
				{items.length === 0 ? (
					<div className={css(styles.empty)}>
						{status === 'loading'
							? 'Loading texts…'
							: status === 'error'
								? "Couldn't load texts. We'll keep trying."
								: 'No unread texts.'}
					</div>
				) : (
					<ul className={css(styles.list)}>
						{items.map(item => (
							<li key={item.conversationId}>
								<Button
									variant='ghost'
									styles={[styles.row]}
									disabled={!item.companyId && !item.e164}
									title={item.companyId ? `Open ${item.companyName ?? 'lead'}` : 'Reply to this number'}
									onClick={() => {
										setOpen(false);
										onOpen(item);
									}}
									data-testid='text-inbox-row'
								>
									<span className={css(styles.rowTop)}>
										<span className={css(styles.name)}>{item.contactName}</span>
										<span className={css(styles.time)}>{formatReceivedAt(item.receivedAt)}</span>
									</span>
									{item.companyName ? (
										<span className={css(styles.company)}>
											{[item.companyName, formatOtherContacts(item.otherContactCount)].filter(Boolean).join(' · ')}
										</span>
									) : null}
									{item.snippet ? <span className={css(styles.snippet)}>{item.snippet}</span> : null}
								</Button>
							</li>
						))}
					</ul>
				)}
			</PopoverContent>
		</Popover>
	);
};
